import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

const mockPostJSON = jest.fn();

jest.mock('lib/request', () => ({
  postJSON: (...args: unknown[]) => mockPostJSON(...args),
}));

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name }: { name: string }) => <span data-testid={`icon-${name}`} />,
}));

interface MockAlertDialogProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  primaryText: string;
  onPrimary: () => void;
  primaryHref?: string;
  secondary?: { prefixText?: string; linkText: string; onClick: () => void };
  iconName: string;
  iconVariant: string;
}

jest.mock('components/styleguide/alert_dialog/AlertDialog', () => ({
  __esModule: true,
  default: ({
    isOpen,
    title,
    message,
    primaryText,
    onPrimary,
    primaryHref,
    secondary,
    iconName,
    iconVariant,
  }: MockAlertDialogProps) => {
    if (!isOpen) return null;
    return (
      <div data-testid="alert-dialog" data-icon={iconName} data-variant={iconVariant}>
        <h3>{title}</h3>
        <div data-testid="alert-message">{message}</div>
        <button data-testid="primary" data-href={primaryHref || ''} onClick={onPrimary}>
          {primaryText}
        </button>
        {secondary && (
          <div>
            {secondary.prefixText && <span>{secondary.prefixText}</span>}
            <button data-testid="secondary" onClick={secondary.onClick}>
              {secondary.linkText}
            </button>
          </div>
        )}
      </div>
    );
  },
}));

import SubscriptionStatusGate from './SubscriptionStatusGate';

const DEFAULT_URL = '/employer/subscription-modals/review/seen';
const ONBOARDING_URL = 'https://campaign.landing.jobs/business-onboarding-platform';

describe('SubscriptionStatusGate', () => {
  beforeEach(() => {
    mockPostJSON.mockReset();
    mockPostJSON.mockResolvedValue({});
  });

  describe('mark-as-seen on view', () => {
    it('posts to markSeenUrl as soon as the modal renders', () => {
      render(
        <SubscriptionStatusGate
          modal="review"
          planName="Growth"
          markSeenUrl={DEFAULT_URL}
          onboardingMeetingUrl={ONBOARDING_URL}
        />
      );

      expect(mockPostJSON).toHaveBeenCalledWith(DEFAULT_URL);
    });
  });

  describe('review modal', () => {
    it('renders with the plan name and closes on primary click', () => {
      render(
        <SubscriptionStatusGate
          modal="review"
          planName="Growth"
          markSeenUrl={DEFAULT_URL}
          onboardingMeetingUrl={ONBOARDING_URL}
        />
      );

      expect(screen.getByText("We're setting up your account")).toBeInTheDocument();
      expect(screen.getByText(/Your Growth plan is being reviewed/)).toBeInTheDocument();

      fireEvent.click(screen.getByTestId('primary'));
      expect(screen.queryByTestId('alert-dialog')).not.toBeInTheDocument();
    });

    it('falls back to a generic message when plan name is null', () => {
      render(
        <SubscriptionStatusGate
          modal="review"
          planName={null}
          markSeenUrl={DEFAULT_URL}
          onboardingMeetingUrl={ONBOARDING_URL}
        />
      );

      expect(screen.getByText(/^Your plan is being reviewed/)).toBeInTheDocument();
    });
  });

  describe('rejected modal', () => {
    it('renders and closes on primary click', () => {
      render(
        <SubscriptionStatusGate
          modal="rejected"
          planName="Growth"
          markSeenUrl="/employer/subscription-modals/rejected/seen"
          onboardingMeetingUrl={ONBOARDING_URL}
        />
      );

      expect(screen.getByText("Your account wasn't approved")).toBeInTheDocument();
      expect(screen.getByText(/weren't able to approve your Growth subscription/)).toBeInTheDocument();

      fireEvent.click(screen.getByTestId('primary'));
      expect(screen.queryByTestId('alert-dialog')).not.toBeInTheDocument();
    });
  });

  describe('welcome modal', () => {
    const props = {
      modal: 'welcome' as const,
      planName: 'Growth',
      markSeenUrl: '/employer/subscription-modals/welcome/seen',
      onboardingMeetingUrl: ONBOARDING_URL,
    };

    it('renders all three bullet points', () => {
      render(<SubscriptionStatusGate {...props} />);

      expect(screen.getByText('Your account was approved')).toBeInTheDocument();
      expect(screen.getByText('Book your onboarding session')).toBeInTheDocument();
      expect(screen.getByText('See how the platform works, start to finish.')).toBeInTheDocument();
      expect(screen.getByText('Get tips on making the most of your features.')).toBeInTheDocument();
      expect(screen.getByText("Meet the team. We're here whenever you need us.")).toBeInTheDocument();
    });

    it('opens the onboarding URL in a new tab and keeps the modal open on primary click', () => {
      const windowOpen = jest.spyOn(window, 'open').mockImplementation();
      render(<SubscriptionStatusGate {...props} />);

      fireEvent.click(screen.getByTestId('primary'));

      expect(windowOpen).toHaveBeenCalledWith(ONBOARDING_URL, '_blank', 'noopener,noreferrer');
      expect(screen.getByTestId('alert-dialog')).toBeInTheDocument();
      windowOpen.mockRestore();
    });

    it('does not call window.open when the onboarding URL is not configured', () => {
      const windowOpen = jest.spyOn(window, 'open').mockImplementation();
      render(<SubscriptionStatusGate {...props} onboardingMeetingUrl={null} />);
      fireEvent.click(screen.getByTestId('primary'));

      expect(windowOpen).not.toHaveBeenCalled();
      windowOpen.mockRestore();
    });

    it('closes the modal when the secondary link is clicked', () => {
      render(<SubscriptionStatusGate {...props} />);

      expect(screen.getByText('Already scheduled your session?')).toBeInTheDocument();
      fireEvent.click(screen.getByText('Go to the platform'));

      expect(screen.queryByTestId('alert-dialog')).not.toBeInTheDocument();
    });
  });
});
